/**
 * Reusable breadcrumb component with built-in JSON-LD BreadcrumbList schema.
 *
 * Google renders breadcrumbs directly in search results when the schema is
 * present, and uses them as a strong signal for site hierarchy. Every
 * content page should expose one.
 *
 * Usage:
 *   <Breadcrumb items={[
 *     { name: "Home", to: "/" },
 *     { name: "Specialties", to: "/specialties" },
 *     { name: "Cardiology" },                       // last item: no link
 *   ]} />
 *
 * The schema absolute URL is built from SITE_BASE_URL (defaults to the
 * production canonical domain).
 */
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CaretRight } from "@phosphor-icons/react";

const SITE_BASE_URL = "https://npilookup.com";

const Breadcrumb = ({ items = [], className = "" }) => {
  if (!items || items.length === 0) return null;

  const schemaItems = items.map((item, idx) => {
    const entry = {
      "@type": "ListItem",
      position: idx + 1,
      name: item.name,
    };
    // Last item shouldn't have an `item` URL per schema.org guidance
    if (item.to && idx < items.length - 1) {
      entry.item = item.to.startsWith("http")
        ? item.to
        : `${SITE_BASE_URL}${item.to.startsWith("/") ? "" : "/"}${item.to}`;
    }
    return entry;
  });

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: schemaItems,
  };

  return (
    <>
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
      </Helmet>
      <nav
        className={`text-sm mb-6 ${className}`}
        aria-label="Breadcrumb"
        data-testid="breadcrumb"
      >
        <ol className="flex items-center gap-1.5 text-slate-500 flex-wrap">
          {items.map((item, idx) => {
            const isLast = idx === items.length - 1;
            return (
              <li key={`${item.name}-${idx}`} className="flex items-center gap-1.5">
                {isLast || !item.to ? (
                  <span
                    className="text-slate-900 font-medium"
                    aria-current={isLast ? "page" : undefined}
                  >
                    {item.name}
                  </span>
                ) : (
                  <Link
                    to={item.to}
                    className="hover:text-teal transition-colors"
                    title={`Go to ${item.name}`}
                  >
                    {item.name}
                  </Link>
                )}
                {!isLast && <CaretRight className="w-3 h-3 text-slate-300" aria-hidden="true" />}
              </li>
            );
          })}
        </ol>
      </nav>
    </>
  );
};

export default Breadcrumb;
